/**
 * Data types for the FCCERS dashboard.
 *
 * `DashboardObservation` mirrors the shape returned by the Lambda GET
 * endpoint's `unflattenRow()` — one object per submitted observation.
 */

export interface SubscaleAverages {
  S1: number | null;
  S2: number | null;
  S3: number | null;
  S4: number | null;
  S5: number | null;
  S6: number | null;
}

/** Per-item score detail inside the JSON blob. */
export interface DetailItem {
  id: string;
  number: number;
  name: string;
  score: number | null;
  itemNa: boolean;
  complete: boolean;
}

/** Per-subscale detail inside the JSON blob. */
export interface DetailSubscale {
  id: string;
  name: string;
  average: number | null;
  scoredItems: number;
  naItems: number;
  pendingItems: number;
  items: DetailItem[];
}

/** Full observation detail stored in column W as JSON. */
export interface ObservationDetail {
  siteAddress: string;
  networkName?: string;
  /** Scoresheet front-page fields (manual p. 83). */
  providerPerson?: string;
  providerLicenseId?: string;
  providersPresent?: number;
  childrenEnrolled?: number;
  enrollmentByAge?: Record<string, number> | null;
  highestAllowedAtOneTime?: number;
  totalEnrolledPastMonth?: number;
  childrenSpecialNeeds?: number;
  specialNeedTypes?: string[];
  specialNeedOther?: string;
  grossMotorIndoors?: string;
  grossMotorOutdoors?: string;
  foodPreferences?: string;
  foodAllergies?: string;
  adultsPresent?: number;
  responses: Record<string, 'Y' | 'N' | 'NA'>;
  itemNa: Record<string, boolean>;
  itemNotes: Record<string, string>;
  subscales: DetailSubscale[];
}

export interface DashboardObservation {
  recordId: string;
  exportedAt: string;
  observationDate: string;
  siteName: string;
  providerName: string;
  observerName: string;
  observerEmail: string;
  childrenPresent: number;
  ageGroupsPresent: string;
  startTime: string;
  endTime: string;
  totalAverage: number | null;
  scoredItems: number;
  naItems: number;
  pendingItems: number;
  subscaleAverages: SubscaleAverages;
  notes: string;
  /** Full detail from JSON blob column — null for legacy rows. */
  detail: ObservationDetail | null;
}

export interface DashboardFilters {
  from: string;
  to: string;
  sites: string[];
  observer: string;
  minScore: number | null;
}

export const SUBSCALE_META: Array<{
  id: keyof SubscaleAverages;
  name: string;
  shortName: string;
  color: string;
}> = [
  { id: 'S1', name: 'Space and Furnishings', shortName: 'Space', color: '#3a86c8' },
  { id: 'S2', name: 'Personal Care Routines', shortName: 'Care', color: '#e36a4d' },
  { id: 'S3', name: 'Language and Books', shortName: 'Language', color: '#c6a24a' },
  { id: 'S4', name: 'Activities', shortName: 'Activities', color: '#8fbf3f' },
  { id: 'S5', name: 'Interaction', shortName: 'Interaction', color: '#1f2b5b' },
  { id: 'S6', name: 'Program Structure', shortName: 'Program', color: '#7c5cbf' },
];
